import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, LogIn, LogOut, Menu, Settings, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteConfig } from '@/contexts/SiteConfigContext';
import { SettingsModal } from '@/components/SettingsModal';
import defaultLogo from '@/pages/homelandlogo-full.png';
import { PublicAssetsSlideOver } from '@/components/admin/PublicAssetsSlideOver';
import { CountryCurrencySelect } from '@/components/CountryCurrencySelect';
import { WhatsAppFloat } from '@/components/WhatsAppFloat';

/** Main bar: Home → Ecosystem, then Academy, then Careers. */
const MAIN_NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/ecosystem', label: 'Ecosystem' },
  { to: '/careers', label: 'Careers' },
] as const;

const FOOTER_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/ecosystem', label: 'Ecosystem' },
  { to: '/courses', label: 'Courses' },
  { to: '/careers', label: 'Careers' },
  { to: '/referral', label: 'Referral' },
  { to: '/contact', label: 'Contact' },
] as const;

const ACADEMY_ACTIVE = (pathname: string) => pathname.startsWith('/courses');

const COLOR_BARS = [
  'bg-africa-red',
  'bg-africa-orange',
  'bg-africa-yellow',
  'bg-africa-green',
  'bg-africa-blue',
  'bg-africa-purple',
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [siteContentOpen, setSiteContentOpen] = useState(false);
  const [academyOpen, setAcademyOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const academyRef = useRef<HTMLDivElement>(null);
  const { user, loading, login, logout } = useAuth();
  const { config: siteConfig, refetch: refetchSiteConfig } = useSiteConfig();
  const isReferralPage = location.pathname.startsWith('/referral');
  const isLessonView = location.pathname.includes('/sections/');
  const isHomeworkPage = location.pathname.startsWith('/homework');
  const isHomePage = location.pathname === '/';

  const normalizeLogoDimension = (value?: string | null, fallback = 'auto') => {
    const trimmed = value?.trim();
    if (!trimmed || trimmed === 'auto') return fallback;
    return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
  };

  const headerLogoUrl = siteConfig?.headerLogoUrl?.trim() || defaultLogo;
  const headerLogoWidth = normalizeLogoDimension(siteConfig?.headerLogoWidth?.trim(), 'auto');
  const headerLogoHeight = normalizeLogoDimension(siteConfig?.headerLogoHeight?.trim(), '48px');
  const headerLogoAlign = (siteConfig?.headerLogoAlign === 'center' ? 'center' : 'left') as 'left' | 'center';
  const footerLogoUrl = siteConfig?.footerLogoUrl?.trim() || '';
  const footerLogoWidth = siteConfig?.footerLogoWidth?.trim();
  const footerLogoHeight = siteConfig?.footerLogoHeight?.trim();
  const footerLogoAlign = (siteConfig?.footerLogoAlign === 'center' ? 'center' : 'left') as 'left' | 'center';
  const connectEmail = siteConfig?.connectEmail?.trim() || '';
  const connectPhone = siteConfig?.connectPhone?.trim() || '';
  const connectAddress = siteConfig?.connectAddress?.trim() || '';
  const connectEmailNairobi = siteConfig?.connectEmailNairobi?.trim() || '';
  const connectPhoneNairobi = siteConfig?.connectPhoneNairobi?.trim() || '';
  const connectAddressNairobi = siteConfig?.connectAddressNairobi?.trim() || '';
  const connectSocialLinks = siteConfig?.connectSocialLinks ?? [];

  // Apply favicon from site config so it displays in browser tab with standard dimensions
  useEffect(() => {
    const url = siteConfig?.faviconUrl?.trim();
    const w = siteConfig?.faviconWidth?.trim();
    const h = siteConfig?.faviconHeight?.trim();
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (url) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = url;
      if (w && h) link.sizes = `${w}x${h}`;
      else link.removeAttribute('sizes');
    } else if (link && link.getAttribute('href')?.startsWith('http')) {
      link.href = '/favicon.svg';
      link.removeAttribute('sizes');
    }
  }, [siteConfig?.faviconUrl, siteConfig?.faviconWidth, siteConfig?.faviconHeight]);

  useEffect(() => {
    if (!avatarOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) setAvatarOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [avatarOpen]);

  useEffect(() => {
    if (!academyOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (academyRef.current && !academyRef.current.contains(e.target as Node)) setAcademyOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [academyOpen]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      {/* Header — hidden on home page (it has its own immersive TopNav) */}
      {!isLessonView && !isHomeworkPage && !isHomePage && (
      <header className="w-full bg-primary sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3 gap-3">
          <div className={`flex-shrink-0 ${headerLogoAlign === 'center' ? 'flex-1 flex justify-center' : ''}`}>
            <Link to="/" className="inline-flex items-center">
              {headerLogoUrl ? (
                <img
                  src={headerLogoUrl}
                  alt="Homeland Ecosystem Hub"
                  className="object-contain"
                  style={{
                    width: headerLogoWidth,
                    height: headerLogoHeight,
                  }}
                />
              ) : (
                <span className="font-display font-bold text-lg md:text-xl text-primary-foreground">
                  HOMELAND <span className="text-accent">Ecosystem Hub</span>
                </span>
              )}
            </Link>
          </div>

          {/* Desktop nav */}
          {!isReferralPage && (
            <nav className="hidden md:flex items-center gap-1 flex-wrap justify-end">
              {MAIN_NAV_LINKS.slice(0, 3).map((link) => {
                const active = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'text-accent'
                        : 'text-primary-foreground/80 hover:text-primary-foreground'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="relative" ref={academyRef}>
                <button
                  type="button"
                  onClick={() => setAcademyOpen((o) => !o)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                    ACADEMY_ACTIVE(location.pathname)
                      ? 'text-accent'
                      : 'text-primary-foreground/80 hover:text-primary-foreground'
                  }`}
                  aria-expanded={academyOpen}
                  aria-haspopup="true"
                >
                  Academy
                  <ChevronDown className={`w-4 h-4 transition-transform ${academyOpen ? 'rotate-180' : ''}`} />
                </button>
                {academyOpen && (
                  <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-xl border border-border bg-card shadow-lg py-1 text-foreground">
                    <Link
                      to="/courses"
                      className="block px-4 py-2.5 text-sm hover:bg-muted"
                      onClick={() => setAcademyOpen(false)}
                    >
                      Courses
                    </Link>
                  </div>
                )}
              </div>
              {MAIN_NAV_LINKS.slice(3).map((link) => {
                const active = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'text-accent'
                        : 'text-primary-foreground/80 hover:text-primary-foreground'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <CountryCurrencySelect className="ml-1" />
              <div className="ml-2 relative" ref={avatarRef}>
                {!loading && (
                  <>
                    {user ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setAvatarOpen((o) => !o)}
                          className="flex items-center gap-2 p-1.5 rounded-full text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                          aria-expanded={avatarOpen}
                          aria-haspopup="true"
                          aria-label="Account menu"
                        >
                          {user.picture_url ? (
                            <img src={user.picture_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <span className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center text-sm font-semibold text-primary-foreground">
                              {user.email?.[0]?.toUpperCase() ?? user.name?.[0]?.toUpperCase() ?? '?'}
                            </span>
                          )}
                          <ChevronDown className={`w-4 h-4 transition-transform ${avatarOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {avatarOpen && (
                          <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] rounded-lg border border-border bg-card shadow-lg py-1 text-foreground">
                            <div className="px-4 py-2 border-b border-border">
                              <p className="text-sm font-medium truncate">{user.email}</p>
                              <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded bg-primary/10 text-primary capitalize">
                                {user.role}
                              </span>
                            </div>
                            {user.role === 'admin' && (
                              <>
                                <Link
                                  to="/admin"
                                  className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
                                  onClick={() => setAvatarOpen(false)}
                                >
                                  <User className="w-4 h-4" /> Admin
                                </Link>
                                <button
                                  type="button"
                                  className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left"
                                  onClick={() => {
                                    setAvatarOpen(false);
                                    setSiteContentOpen(true);
                                  }}
                                >
                                  <Settings className="w-4 h-4" /> Edit public site
                                </button>
                              </>
                            )}
                            {user.role === 'tutor' && (
                              <Link
                                to="/tutor/dashboard"
                                className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
                                onClick={() => setAvatarOpen(false)}
                              >
                                <LayoutDashboard className="w-4 h-4" /> Dashboard
                              </Link>
                            )}
                            {user.role === 'student' && (
                              <Link
                                to="/dashboard"
                                className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
                                onClick={() => setAvatarOpen(false)}
                              >
                                <LayoutDashboard className="w-4 h-4" /> Dashboard
                              </Link>
                            )}
                            <Link
                              to="/courses"
                              className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
                              onClick={() => setAvatarOpen(false)}
                            >
                              Courses
                            </Link>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left"
                              onClick={() => {
                                setAvatarOpen(false);
                                setSettingsOpen(true);
                              }}
                            >
                              <Settings className="w-4 h-4" /> Account settings
                            </button>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left"
                              onClick={() => { setAvatarOpen(false); logout(); }}
                            >
                              <LogOut className="w-4 h-4" /> Log out
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={login}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                      >
                        <LogIn className="w-4 h-4" /> Log in
                      </button>
                    )}
                  </>
                )}
              </div>
            </nav>
          )}

          {/* Mobile: avatar + hamburger both toggle same dropdown (hidden on referral page) */}
          {!isReferralPage && (
            <div className="md:hidden flex items-center gap-2 relative">
              {!loading && (
                user ? (
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen((o) => !o)}
                    className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center text-sm font-semibold text-primary-foreground overflow-hidden shrink-0"
                    aria-label="Open menu"
                    aria-expanded={mobileMenuOpen}
                  >
                    {user.picture_url ? (
                      <img src={user.picture_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      user.email?.[0]?.toUpperCase() ?? user.name?.[0]?.toUpperCase() ?? '?'
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={login}
                    className="w-8 h-8 rounded-full bg-primary-foreground/10 flex items-center justify-center text-primary-foreground"
                    aria-label="Log in"
                  >
                    <LogIn className="w-4 h-4" />
                  </button>
                )
              )}
              <button
                type="button"
                className="p-2 text-primary-foreground"
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((o) => !o)}
              >
                <Menu className="w-6 h-6" />
              </button>
              {mobileMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" aria-hidden onClick={() => setMobileMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] rounded-lg border border-border bg-card shadow-lg py-1">
                    <nav className="flex flex-col">
                      {MAIN_NAV_LINKS.slice(0, 3).map((link) => {
                        const active = location.pathname === link.to;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                              active ? 'text-accent' : 'text-foreground hover:bg-secondary'
                            }`}
                          >
                            {link.label}
                          </Link>
                        );
                      })}
                      <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Academy
                      </div>
                      <Link
                        to="/courses"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                          location.pathname.startsWith('/courses') ? 'text-accent' : 'text-foreground hover:bg-secondary'
                        }`}
                      >
                        Courses
                      </Link>
                      {MAIN_NAV_LINKS.slice(3).map((link) => {
                        const active = location.pathname === link.to;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                              active ? 'text-accent' : 'text-foreground hover:bg-secondary'
                            }`}
                          >
                            {link.label}
                          </Link>
                        );
                      })}
                      <div className="px-3 py-2 border-t border-border mt-1">
                        <CountryCurrencySelect variant="panel" />
                      </div>
                      {!loading && (
                        <div className="border-t border-border mt-2 pt-2 px-2">
                          {user ? (
                            <>
                              <p className="px-3 py-1.5 text-xs text-muted-foreground truncate">{user.email}</p>
                              <span className="inline-block mx-3 mb-2 text-xs font-medium px-2 py-0.5 rounded bg-primary/10 text-primary capitalize">{user.role}</span>
                              {user.role === 'admin' && (
                                <>
                                  <Link
                                    to="/admin"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg"
                                  >
                                    <User className="w-4 h-4" /> Admin
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMobileMenuOpen(false);
                                      setSiteContentOpen(true);
                                    }}
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg text-left"
                                  >
                                    <Settings className="w-4 h-4" /> Edit public site
                                  </button>
                                </>
                              )}
                              {user.role === 'tutor' && (
                                <Link to="/tutor/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg">
                                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                                </Link>
                              )}
                              {user.role === 'student' && (
                                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg">
                                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                                </Link>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setMobileMenuOpen(false);
                                  setSettingsOpen(true);
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg text-left"
                              >
                                <Settings className="w-4 h-4" /> Account settings
                              </button>
                              <button type="button" className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg text-left" onClick={() => { setMobileMenuOpen(false); logout(); }}>
                                <LogOut className="w-4 h-4" /> Log out
                              </button>
                            </>
                          ) : (
                            <button type="button" onClick={() => { setMobileMenuOpen(false); login(); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted rounded-lg">
                              <LogIn className="w-4 h-4" /> Log in
                            </button>
                          )}
                        </div>
                      )}
                    </nav>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Color accent bar (header only) */}
        <div className="flex h-[3px]">
          {COLOR_BARS.map((c, i) => (
            <div key={i} className={`flex-1 ${c}`} />
          ))}
        </div>
      </header>
      )}

      {/* Settings modal (all roles) */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {/* Public site content editor (admin only, but safe to mount always) */}
      <PublicAssetsSlideOver
        open={siteContentOpen}
        onClose={() => setSiteContentOpen(false)}
        onSaved={() => {
          void refetchSiteConfig();
        }}
      />

      {/* Main content */}
      <main className="flex-1">{children}</main>

      {/* Footer — hidden on home page (it has its own footer) */}
      {!isHomePage && (
      <footer className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className={footerLogoAlign === 'center' ? 'flex flex-col items-center text-center' : ''}>
              {footerLogoUrl ? (
                <a href="/" className="inline-block mb-4">
                  <img
                    src={footerLogoUrl}
                    alt="Homeland Ecosystem Hub"
                    className="object-contain"
                    style={{
                      width: footerLogoWidth ? `${Math.min(600, Math.max(1, Number(footerLogoWidth)))}px` : undefined,
                      height: footerLogoHeight ? `${Math.min(200, Math.max(1, Number(footerLogoHeight)))}px` : undefined,
                      ...((!footerLogoWidth && !footerLogoHeight) ? { height: '3.5rem', maxHeight: '80px', maxWidth: '260px' } : {}),
                    }}
                  />
                </a>
              ) : (
                <p className="font-display font-bold text-lg text-primary-foreground mb-4">Homeland Ecosystem Hub</p>
              )}
              <p className="text-primary-foreground/60 text-sm leading-relaxed">
                Empowering the next generation of innovators, entrepreneurs, and changemakers.
              </p>
            </div>
            <div>
              <h4 className="font-display font-semibold mb-3">Quick Links</h4>
              <div className="space-y-2">
                {FOOTER_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-sm text-primary-foreground/60 hover:text-accent transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-display font-semibold mb-3">Connect</h4>
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-foreground/50 mb-2">
                    Eldoret
                  </p>
                  {connectEmail && <p className="text-sm text-primary-foreground/60">{connectEmail}</p>}
                  {connectAddress && <p className="text-sm text-primary-foreground/60 mt-1">{connectAddress}</p>}
                  {connectPhone && <p className="text-sm text-primary-foreground/60 mt-1">{connectPhone}</p>}
                </div>
                {(connectEmailNairobi || connectPhoneNairobi || connectAddressNairobi) && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-foreground/50 mb-2">
                      Nairobi
                    </p>
                    {connectEmailNairobi && (
                      <p className="text-sm text-primary-foreground/60">{connectEmailNairobi}</p>
                    )}
                    {connectAddressNairobi && (
                      <p className="text-sm text-primary-foreground/60 mt-1">{connectAddressNairobi}</p>
                    )}
                    {connectPhoneNairobi && (
                      <p className="text-sm text-primary-foreground/60 mt-1">{connectPhoneNairobi}</p>
                    )}
                  </div>
                )}
              </div>
              {connectSocialLinks.length > 0 && (
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {connectSocialLinks.filter((s) => s.url?.trim()).map((s) => (
                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="text-primary-foreground/60 hover:text-accent transition-colors" aria-label={s.label}>
                      {s.id === 'twitter' && <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>}
                      {s.id === 'linkedin' && <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>}
                      {s.id === 'facebook' && <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>}
                      {s.id === 'instagram' && <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.265.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.058 1.645-.07 4.849-.07zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>}
                      {!['twitter', 'linkedin', 'facebook', 'instagram'].includes(s.id) && <span className="text-xs">{s.label || 'Link'}</span>}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-primary-foreground/10 mt-8 pt-6 text-center text-xs text-primary-foreground/40">
            © {new Date().getFullYear()} Homeland Ecosystem Hub. All rights reserved.
          </div>
        </div>
      </footer>
      )}

      {!isLessonView && !isHomeworkPage && <WhatsAppFloat />}
    </div>
  );
}
